import React, { useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { Box, IconButton } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { routes } from "Constant/routes";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    getDashboardDestinationIcon,
    getDashboardSourceIcon,
    getReportPath,
    getTrackerNameById,
    getVisibleMobileTrackers,
    textCharacterLimit,
} from "helpers/dashboard/homeDashboard";

import EllipsisText from "components/EllipsisText";
import { GenericModal } from "components/GenericModal";
import SupportCard from "components/molecules/SupportCard";

import PageContainer from "../../components/atoms/PageContainer";
import StandartButton from "../../components/atoms/StandartButton";
import { ButtonVariant } from "../../Constant/button";
import {
    getDestinationsDashboardDatasRedux,
    getSourcesDashboardDatasRedux,
} from "../../redux/slices/dashboardDatas/dashboardDatas.asyncActions";
import { getAllDestinationsRedux } from "../../redux/slices/destinations/destinations.asyncActions";
import { getAllSourcesRedux } from "../../redux/slices/sources/sources.asyncActions";
import { getExcistTrackersRedux } from "../../redux/slices/trackers/trackers.asyncActions";
import {
    deleteLastAddedDest,
    deleteLastAddedSource,
} from "../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";
import TrackerTable from "../Trackers/TrackerTable";
import FlowDiagram from "./FlowDiagram";
import {
    FlowPanelLoading,
    LoadingCard,
    LoadingColumn,
    StatCardSkeleton,
} from "./FlowDiagram.styled";
import "./home.css";
import {
    AnalyticsCardsRow,
    CardSlot,
    CardsRow,
    DashboardCard,
    HomeTrackersMobile,
    OverviewActionButton,
    OverviewActionLabel,
    OverviewActionLink,
    OverviewCardActions,
    OverviewCardBody,
    OverviewCardContent,
    OverviewCardHeader,
    OverviewCardSkeleton,
    OverviewCardTitle,
    OverviewCardTitleText,
    OverviewIconChip,
    ReportListItem,
    ReportListItemChevron,
    ReportListItemMeta,
    ReportListItemName,
    ReportsHighlight,
    ReportsHighlightCopy,
    ReportsHighlightSubtitle,
    ReportsHighlightTitle,
    ReportsList,
    SectionBlock,
    SectionTitle,
    ShowMoreTrackersWrap,
    StatCardSlot,
    StatHeader,
    StatIconChip,
    StatLabel,
    StatValue,
} from "./Home.styled";
import Templates from "./Templates";
import useHomeDashboardData from "./useHomeDashboardData";

const overviewCardMotion = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 16 },
    transition: { duration: 0.25, ease: "easeOut" },
};

const MotionCardSlot = motion(CardSlot);

const StatCard = ({ icon, label, value }) => (
    <DashboardCard>
        <CardContent sx={{ position: "relative" }}>
            <StatHeader>
                <StatLabel>
                    <EllipsisText text={label} width="100%" />
                </StatLabel>
                <StatIconChip $color="#000000" $bg="rgba(0, 0, 0, 0.02)">
                    {icon}
                </StatIconChip>
            </StatHeader>
            <StatValue>
                <EllipsisText text={String(value)} width="100%" />
            </StatValue>
        </CardContent>
    </DashboardCard>
);

const Home = () => {
    const activeTrackers = useSelector(
        (state) => state.allTrackersState.trackers,
    );
    const destinations = useSelector(
        (state) =>
            state.allDashboardDatasState.getAllDashboardDatas.destinations,
    );
    const language = useSelector((state) => state.languagesState.languages);
    const sources = useSelector(
        (state) => state.allDashboardDatasState.getAllDashboardDatas.sources,
    );
    const dashboardDatasStatus = useSelector(
        (state) => state.allDashboardDatasState.getAllDashboardDatas.status,
    );

    const [trackersCheckStatu, setTrackersCheckStatu] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showAllMobileTrackers, setShowAllMobileTrackers] = useState(false);
    const {
        trackerAllEvents,
        trackerAllEventsNum,
        leadsAllEvents,
        leadsAllEventsNum,
        signalsLoading,
        checkCardStatus,
        goingTrackerCardStatus,
        readyCardLinks,
        metaLoading,
        reportsLoading,
        dismissCheckCard,
        dismissGoingTrackerCard,
    } = useHomeDashboardData(language);

    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
    const isOverviewSingleColumn = useMediaQuery("(max-width:600px)");
    const isOverviewTwoColumns = useMediaQuery("(max-width:1100px)");
    const isOverviewThreeColumns = useMediaQuery("(max-width:1400px)");

    const statsLoading =
        signalsLoading ||
        !trackersCheckStatu ||
        dashboardDatasStatus === "loading";

    const statCardCount = statsLoading || leadsAllEventsNum > 0 ? 5 : 4;

    const overviewLoading = statsLoading || metaLoading || reportsLoading;
    const overviewSkeletonCount = isOverviewSingleColumn
        ? 1
        : isOverviewTwoColumns
          ? 2
          : isOverviewThreeColumns
            ? 3
            : 4;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        remainingCount: remainingMobileTrackers,
        visibleTrackers: visibleMobileTrackers,
    } = getVisibleMobileTrackers(activeTrackers, showAllMobileTrackers);

    useEffect(() => {
        dispatch(getExcistTrackersRedux()).then(() => {
            setTrackersCheckStatu(true);
        });
        dispatch(getDestinationsDashboardDatasRedux());
        dispatch(getSourcesDashboardDatasRedux());
    }, [dispatch]);

    const newSources = useSelector(
        (state) => state.allSourcesState.getAllSources.sources,
    );
    const newDestination = useSelector(
        (state) => state.allDestinationsState.getAllDestinations.destinations,
    );

    useEffect(() => {
        return () => {
            dispatch(deleteLastAddedDest());
            dispatch(deleteLastAddedSource());
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(getAllSourcesRedux());
        dispatch(getAllDestinationsRedux());
    }, [dispatch]);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const getSourceIcon = (id) => getDashboardSourceIcon(newSources, id);

    const goToTrackers = () => {
        navigate(routes.trackers);
    };

    const goToTracker = (id) => {
        navigate(`tracker/${id}`);
    };

    const getDestinationIconHandler = (id) =>
        getDashboardDestinationIcon(newDestination, id);

    return (
        <>
            <PageContainer
                title={"Dashboard"}
                additionalButtons={
                    <StandartButton
                        data-cy="home-recent-activities"
                        variant={ButtonVariant.BLACK_OUTLINED}
                        onClick={() => {
                            navigate(routes.activity_log);
                        }}
                    >
                        {"RECENT ACTIVITIES"}
                    </StandartButton>
                }
                showBackButton={false}
            >
                <SectionBlock>
                    <AnalyticsCardsRow $count={statCardCount}>
                        {statsLoading
                            ? [0, 1, 2, 3, 4].map((i) => (
                                  <StatCardSlot key={`stat-skeleton-${i}`}>
                                      <StatCardSkeleton />
                                  </StatCardSlot>
                              ))
                            : [
                                  <StatCardSlot
                                      key="signals"
                                      data-cy="home-stat-signals"
                                  >
                                      <StatCard
                                          icon={<WebhookOutlinedIcon />}
                                          label={
                                              language?.dashboard
                                                  ?.signal_count_card?.title ??
                                              "Total Signals Count"
                                          }
                                          value={trackerAllEvents}
                                      />
                                  </StatCardSlot>,
                                  leadsAllEventsNum > 0 && (
                                      <StatCardSlot
                                          key="leads"
                                          data-cy="home-stat-leads"
                                      >
                                          <StatCard
                                              icon={<PersonAddIcon />}
                                              label={
                                                  language?.dashboard
                                                      ?.lead_count_card
                                                      ?.title ??
                                                  "Total Leads Count"
                                              }
                                              value={leadsAllEvents}
                                          />
                                      </StatCardSlot>
                                  ),
                                  <StatCardSlot
                                      key="trackers"
                                      data-cy="home-stat-trackers"
                                  >
                                      <StatCard
                                          icon={<VisibilityIcon />}
                                          label={
                                              language?.dashboard
                                                  ?.tracker_count_card?.title ??
                                              "Active Trackers"
                                          }
                                          value={activeTrackers?.length}
                                      />
                                  </StatCardSlot>,
                                  <StatCardSlot
                                      key="sources"
                                      data-cy="home-stat-sources"
                                  >
                                      <StatCard
                                          icon={<PublicOutlinedIcon />}
                                          label={
                                              language?.dashboard
                                                  ?.source_count_card?.title ??
                                              "Active Sources"
                                          }
                                          value={sources?.length}
                                      />
                                  </StatCardSlot>,
                                  <StatCardSlot
                                      key="destinations"
                                      data-cy="home-stat-destinations"
                                  >
                                      <StatCard
                                          icon={<ShareIcon />}
                                          label={
                                              language?.dashboard
                                                  ?.destination_count_card
                                                  ?.title ??
                                              "Active Destinations"
                                          }
                                          value={destinations?.length}
                                      />
                                  </StatCardSlot>,
                              ]}
                    </AnalyticsCardsRow>
                </SectionBlock>

                <SectionBlock>
                    <SectionTitle>
                        {language?.home_page?.overview ?? "Overview"}
                    </SectionTitle>
                    <CardsRow>
                        <AnimatePresence mode="popLayout" initial={false}>
                            {overviewLoading ? (
                                Array.from({
                                    length: overviewSkeletonCount,
                                }).map((_, index) => (
                                    <MotionCardSlot
                                        key={`overview-skeleton-${index}`}
                                        layout
                                        {...overviewCardMotion}
                                    >
                                        <OverviewCardSkeleton />
                                    </MotionCardSlot>
                                ))
                            ) : (
                                <MotionCardSlot
                                    key="support-card"
                                    layout
                                    {...overviewCardMotion}
                                >
                                    <SupportCard
                                        trackerAllEventsNum={
                                            trackerAllEventsNum
                                        }
                                    />
                                </MotionCardSlot>
                            )}

                            {!overviewLoading && checkCardStatus && (
                                <MotionCardSlot
                                    key="expired-card"
                                    layout
                                    {...overviewCardMotion}
                                >
                                    <DashboardCard>
                                        <OverviewCardContent>
                                            <OverviewCardHeader>
                                                <OverviewIconChip $tone="danger">
                                                    <WarningAmberOutlinedIcon />
                                                </OverviewIconChip>
                                                <IconButton
                                                    data-cy="home-dismiss-expired-trackers"
                                                    size="small"
                                                    onClick={dismissCheckCard}
                                                    aria-label="Dismiss"
                                                    sx={{
                                                        color: "rgba(0,0,0,0.35)",
                                                        flexShrink: 0,
                                                        position: "relative",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </OverviewCardHeader>
                                            <OverviewCardTitle $tone="danger">
                                                <OverviewCardTitleText>
                                                    {language?.dashboard
                                                        ?.expired_tracker_needs_checked_card
                                                        ?.title ??
                                                        "Attention Required: Tracker Issue"}
                                                </OverviewCardTitleText>
                                            </OverviewCardTitle>
                                            <OverviewCardBody $tone="danger">
                                                {textCharacterLimit(
                                                    language?.dashboard
                                                        ?.expired_tracker_needs_checked_card
                                                        ?.description ??
                                                        "There seems to be an issue with the source or destination of your tracker. Please check and reconnect any expired connections to ensure optimal performance.",
                                                )}
                                            </OverviewCardBody>
                                            <OverviewCardActions $tone="danger">
                                                <OverviewActionLink
                                                    to={`${routes.trackers}?filter_expired=1`}
                                                >
                                                    <OverviewActionLabel>
                                                        {"GO TO TRACKER"}
                                                    </OverviewActionLabel>
                                                    <ArrowForwardIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </OverviewActionLink>
                                            </OverviewCardActions>
                                        </OverviewCardContent>
                                    </DashboardCard>
                                </MotionCardSlot>
                            )}

                            {!overviewLoading && goingTrackerCardStatus && (
                                <MotionCardSlot
                                    key="incomplete-card"
                                    layout
                                    {...overviewCardMotion}
                                >
                                    <DashboardCard>
                                        <OverviewCardContent>
                                            <OverviewCardHeader>
                                                <OverviewIconChip $tone="warning">
                                                    <WebhookOutlinedIcon />
                                                </OverviewIconChip>
                                                <IconButton
                                                    data-cy="home-dismiss-incomplete-trackers"
                                                    size="small"
                                                    onClick={
                                                        dismissGoingTrackerCard
                                                    }
                                                    aria-label="Dismiss"
                                                    sx={{
                                                        color: "rgba(0,0,0,0.35)",
                                                        flexShrink: 0,
                                                        position: "relative",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </OverviewCardHeader>
                                            <OverviewCardTitle $tone="warning">
                                                <OverviewCardTitleText>
                                                    {language?.dashboard
                                                        ?.incomplete_tracker_card
                                                        ?.title ??
                                                        "Incomplete Trackers"}
                                                </OverviewCardTitleText>
                                            </OverviewCardTitle>
                                            <OverviewCardBody
                                                $tone="warning"
                                                dangerouslySetInnerHTML={{
                                                    __html: textCharacterLimit(
                                                        language?.dashboard
                                                            ?.incomplete_tracker_card
                                                            ?.description ??
                                                            "It appears that your tracker is missing a source or destination. Complete this connection to fully leverage the benefits of signal conversions!",
                                                    ),
                                                }}
                                            />
                                            <OverviewCardActions $tone="warning">
                                                <OverviewActionLink
                                                    to={`${routes.trackers}?filter_completed=0`}
                                                >
                                                    <OverviewActionLabel>
                                                        {"GO TO TRACKER"}
                                                    </OverviewActionLabel>
                                                    <ArrowForwardIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </OverviewActionLink>
                                            </OverviewCardActions>
                                        </OverviewCardContent>
                                    </DashboardCard>
                                </MotionCardSlot>
                            )}

                            {!overviewLoading && readyCardLinks.length > 0 && (
                                <MotionCardSlot
                                    key="reports-card"
                                    layout
                                    {...overviewCardMotion}
                                >
                                    <DashboardCard>
                                        <OverviewCardContent>
                                            <OverviewIconChip>
                                                <ArticleOutlinedIcon />
                                            </OverviewIconChip>
                                            <OverviewCardTitle>
                                                <OverviewCardTitleText>
                                                    {language?.dashboard
                                                        ?.ready_card?.title ??
                                                        "New Reports Available"}
                                                </OverviewCardTitleText>
                                            </OverviewCardTitle>
                                            <OverviewCardBody>
                                                {language?.dashboard?.ready_card
                                                    ?.description ??
                                                    "Your automatically generated AI reports are ready. Open the list to review and download each PDF."}
                                            </OverviewCardBody>

                                            <ReportsHighlight>
                                                <ReportsHighlightCopy>
                                                    <ReportsHighlightTitle>
                                                        {readyCardLinks.length ===
                                                        1
                                                            ? (language
                                                                  ?.dashboard
                                                                  ?.ready_card
                                                                  ?.highlight_title_singular ??
                                                              "1 report ready")
                                                            : (language?.dashboard?.ready_card?.highlight_title_plural?.replace(
                                                                  "{count}",
                                                                  String(
                                                                      readyCardLinks.length,
                                                                  ),
                                                              ) ??
                                                              `${readyCardLinks.length} reports ready`)}
                                                    </ReportsHighlightTitle>
                                                    <ReportsHighlightSubtitle>
                                                        {language?.dashboard
                                                            ?.ready_card
                                                            ?.highlight_subtitle ??
                                                            "Active AI PDF reports available for download"}
                                                    </ReportsHighlightSubtitle>
                                                </ReportsHighlightCopy>
                                            </ReportsHighlight>

                                            <OverviewCardActions>
                                                <OverviewActionButton
                                                    data-cy="home-see-reports"
                                                    type="button"
                                                    onClick={openModal}
                                                >
                                                    <OverviewActionLabel>
                                                        {language?.dashboard
                                                            ?.see_reports ??
                                                            "SEE REPORTS"}
                                                    </OverviewActionLabel>
                                                    <ArrowForwardIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </OverviewActionButton>
                                            </OverviewCardActions>
                                        </OverviewCardContent>
                                    </DashboardCard>
                                </MotionCardSlot>
                            )}
                        </AnimatePresence>
                    </CardsRow>
                </SectionBlock>

                {!trackersCheckStatu ? (
                    <FlowPanelLoading>
                        <LoadingColumn>
                            {[0, 1, 2].map((i) => (
                                <LoadingCard key={`source-skeleton-${i}`} />
                            ))}
                        </LoadingColumn>
                        <LoadingColumn>
                            {[0, 1, 2].map((i) => (
                                <LoadingCard
                                    key={`destination-skeleton-${i}`}
                                />
                            ))}
                        </LoadingColumn>
                    </FlowPanelLoading>
                ) : (
                    activeTrackers &&
                    activeTrackers.length > 0 &&
                    (isMobileOrTablet ? (
                        <HomeTrackersMobile>
                            <TrackerTable
                                trackers={visibleMobileTrackers}
                                getSourceIcon={getSourceIcon}
                                getDestinationIcon={getDestinationIconHandler}
                                onNavigateTracker={goToTracker}
                                language={language}
                                trackerNameTitle={
                                    language?.trackers?.tracker_name ??
                                    "Tracker Name"
                                }
                                sourcesTitle={
                                    language?.trackers?.sources_title ??
                                    language?.home_page?.sources_title ??
                                    "Sources"
                                }
                                destinationsTitle={
                                    language?.trackers?.destinations_title ??
                                    language?.home_page?.destinations_title ??
                                    "Destinations"
                                }
                                statusTitle={
                                    language?.trackers?.status ?? "Status"
                                }
                                healthyLabel={
                                    language?.trackers?.healthy ?? "Healthy"
                                }
                                attentionLabel={
                                    language?.trackers?.needs_attention ??
                                    "Needs attention"
                                }
                                idleLabel={
                                    language?.trackers?.no_connections ??
                                    "No connections"
                                }
                                noSourcesLabel={
                                    language?.trackers?.none ?? "None"
                                }
                                noDestinationsLabel={
                                    language?.trackers?.none ?? "None"
                                }
                                viewLabel={language?.trackers?.view ?? "View"}
                                emptyStateLabel={
                                    language?.trackers?.no_trackers_found ??
                                    "No trackers found."
                                }
                                enablePagination={false}
                                columnToggle={false}
                            />
                            {!showAllMobileTrackers &&
                                remainingMobileTrackers > 0 && (
                                    <ShowMoreTrackersWrap>
                                        <StandartButton
                                            data-cy="home-show-all-trackers"
                                            variant={
                                                ButtonVariant.BLACK_OUTLINED
                                            }
                                            onClick={() =>
                                                setShowAllMobileTrackers(true)
                                            }
                                        >
                                            {language?.home_page?.show_all_trackers_more?.replace(
                                                "{count}",
                                                String(remainingMobileTrackers),
                                            ) ??
                                                `Show all ${remainingMobileTrackers} more`}
                                        </StandartButton>
                                    </ShowMoreTrackersWrap>
                                )}
                        </HomeTrackersMobile>
                    ) : (
                        <FlowDiagram
                            trackers={activeTrackers}
                            getSourceIcon={getSourceIcon}
                            getDestinationIcon={getDestinationIconHandler}
                            onNavigateTracker={goToTracker}
                            onAddSource={goToTrackers}
                            onAddDestination={goToTrackers}
                            sourcesTitle={
                                language?.home_page?.sources_title ?? "Sources"
                            }
                            destinationsTitle={
                                language?.home_page?.destinations_title ??
                                "Destinations"
                            }
                            addSourceLabel={
                                language?.dashboard?.add_source ?? "Add Source"
                            }
                            addDestinationLabel={
                                language?.dashboard?.add_destination ??
                                "Add Destination"
                            }
                        />
                    ))
                )}

                <Templates />
            </PageContainer>

            <GenericModal
                open={isModalOpen}
                onClose={closeModal}
                title={
                    language?.dashboard?.ready_card?.modal_title ??
                    "Available Reports"
                }
                subtitle={
                    language?.dashboard?.ready_card?.modal_subtitle ??
                    `${readyCardLinks.length} report${
                        readyCardLinks.length === 1 ? "" : "s"
                    } ready to view`
                }

                maxWidth="sm"
                contentPadding={0}
                content={
                    <ReportsList>
                        {readyCardLinks.map((item) => {
                            const trackerName = getTrackerNameById(
                                activeTrackers,
                                item.tracker_id,
                            );

                            return (
                                <ReportListItem
                                    data-cy="home-report-item"
                                    data-report-tracker-id={item.tracker_id}
                                    key={item.tracker_id}
                                    to={getReportPath(item)}
                                    onClick={closeModal}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <ReportListItemName>
                                            {item.name}
                                        </ReportListItemName>
                                        <ReportListItemMeta>
                                            {trackerName ??
                                                `Tracker #${item.tracker_id}`}
                                        </ReportListItemMeta>
                                    </Box>
                                    <ReportListItemChevron>
                                        <ChevronRightIcon />
                                    </ReportListItemChevron>
                                </ReportListItem>
                            );
                        })}
                    </ReportsList>
                }
            />
        </>
    );
};

export default Home;
